import moment from "moment";

import { ClinicianDTO, LabMessageDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { IMyleObjectType, MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FileTypeLabPdf } from "@/ExportableFile/Models/FileTypes/FileTypeLabPdf";

class LabMessage implements IPortalShareableItem, IMyleObjectType {
    public Model: LabMessageDTO;

    // PreFormatted Data for LabInbox View
    public ClinicianPrescriptorName: string;
    public PatientName: string;
    public IsAbnormal: boolean;

    private static readonly ClinicianTypePrescriber = 4;
    private static readonly ClinicianTypeCarbonCopy = 5;

    public constructor(labMessageDTO?: LabMessageDTO) {
        if (labMessageDTO) {
            this.Model = labMessageDTO;
            this.InitFromModel();
        }
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.Result;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.getSharedItem(), this.getSharedItemMessages());
    }

    public getSharedItemId(): string {
        const sharedItem = this.getSharedItem();
        return sharedItem && sharedItem.SharedItemId
            ? sharedItem.SharedItemId
            : null;
    }
    public getSharedItem(): SharedItem {
        return this.Model.SharedItem && this.Model.SharedItem.SharedItemId
            ? this.Model.SharedItem
            : null;
    }

    public getSharedItemMessages(): SharedItemMessage[] {
        return this.Model.PortalMessages || null;
    }

    public getItemTypeHeader(): string {
        return cacheStore.GetString("LabsLabel");
    }

    public getItemTitle(): string {
        return this.Model.RequestSubjects
            ?.truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return this.getItemTitle();
    }

    public getPortalObjectId(): string {
        return this.Model.LabFillerID;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.Result;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalSyncStatus.Unknown;
    }

    public getPrescriptorDTO(): ClinicianDTO | null {
        return this.Model?.Clinicians?.firstOrDefault(p => p.Type === LabMessage.ClinicianTypePrescriber);
    }

    public getCarbonCopiesCDTO(): ClinicianDTO[] | null {
        return this.Model?.Clinicians?.filter(p => p.Type === LabMessage.ClinicianTypeCarbonCopy);
    }

    public getFormattedPrescriptor(): string {
        const prescriptor = this.getPrescriptorDTO();
        return prescriptor
            ? this.getClinicianName(prescriptor)
            : "";
    }

    public getClinicianName(clinician: ClinicianDTO): string {
        return clinician
            ? `${clinician.Prefix || ""} ${clinician.Firstname} ${clinician.Lastname} (${clinician.ID_Number})`.trim()
            : null;
    }

    public getFormattedCC(): string[] {
        const labMessage = this.Model;
        if (!labMessage || !labMessage.Clinicians)
            return [];

        return labMessage.Clinicians
            .filter(p => p.Type === LabMessage.ClinicianTypeCarbonCopy)
            .map(p => this.getClinicianName(p));
    }

    public convertEncapsulatedDataToExportablePDF(): ExportableFileModel[] {
        if (!this.Model || !this.Model.HasEncapsulatedData)
            return [];

        let files = this.Model.LinkedEmptyEncapsulatedData.map(encapsulatedData =>
            new FileTypeLabPdf({
                EncapsulatedDataId: encapsulatedData.Guid,
                LabfillerId: this.Model.LabFillerID,
                description: this.Model.Description,
                date: moment(this.Model.EmittedDateTime).format("YYYY/MM/DD")
            }).toExportableFileModel()
        );

        return files;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        if (!this.Model.SharedItem?.LabSyncMessageDateTime || IsNullOrWhiteSpace(this.Model.EmittedDateTime))
            return false;

        const labSyncDate = moment(this.Model.SharedItem.LabSyncMessageDateTime);
        const emittedDateTime = moment(this.Model.EmittedDateTime);

        return emittedDateTime > labSyncDate;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }

    private InitFromModel(): void { // Init Fields for Inbox
        if (!this.Model)
            return;

        let prescriptor = this.Model.Clinicians.firstOrDefault(p => p.Type == 4);

        this.ClinicianPrescriptorName = prescriptor && `${prescriptor.Lastname} ${prescriptor.Firstname}`;
        this.PatientName = NameHelper.formatFullNameForHasName(this.Model.Patient, NameHelperFormat.FirstNameFirst);
        this.IsAbnormal = this.Model.Abnormality == 1;
    }
}

export { LabMessage };
